import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useEffect, useState, type FC } from 'react';
import { MdClose, MdSearch } from 'react-icons/md';

type Props = {
  value?: string;
  onChange: (value: string | undefined) => void;
};

/**
 * Debounced (300ms) full-text search input for the feed, with a clear button.
 * Soft filled style matching the panel search inputs: grey fill, no border
 * until hover, white with a primary ring when focused.
 */
export const FeedSearchInput: FC<Props> = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState(value ?? '');

  // Sync local input when the parent clears the search (e.g. clear-all button)
  useEffect(() => {
    if (!value) {
      setInputValue('');
    }
  }, [value]);

  // Debounce: propagate 300ms after the user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if ((value ?? '') !== inputValue) {
        onChange(inputValue || undefined);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  return (
    <TextField
      size="small"
      fullWidth
      placeholder="Search posts…"
      value={inputValue}
      onChange={e => setInputValue(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: '4px' }}>
            <MdSearch size={19} color="#9a9a9a" />
          </InputAdornment>
        ),
        endAdornment: inputValue ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              edge="end"
              aria-label="Clear search"
              onClick={() => {
                setInputValue('');
                onChange(undefined);
              }}
            >
              <MdClose size={16} />
            </IconButton>
          </InputAdornment>
        ) : null,
        sx: {
          fontFamily: 'Figtree, sans-serif',
          // Bigger, app-like search on phones; compact toolbar field from md up.
          fontSize: { xs: 15, md: 13.5 },
          height: { xs: 46, md: 40 },
          bgcolor: '#f6f6f6',
          transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
          // Theme forces OutlinedInput to 20px; match the selector to override.
          '&.MuiOutlinedInput-root': { borderRadius: { xs: '12px', md: '10px' } },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
          '&.Mui-focused': {
            bgcolor: 'common.white',
            boxShadow: theme => `0 0 0 3px ${theme.palette.primary.main}14`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: 1,
          },
          '& .MuiOutlinedInput-input::placeholder': { color: '#9a9a9a', opacity: 1 },
        },
      }}
    />
  );
};
